import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/handball";

/**
 * TournamentDialog
 *   mode="create": skapa ny turnering/serie
 *   mode="edit":   redigera befintlig (prop `tournament` krävs)
 * onSaved(tournament) kallas efter lyckad create/edit.
 */
export default function TournamentDialog({
  open,
  onOpenChange,
  mode = "create",
  tournament = null,
  onSaved,
}) {
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && tournament) {
      setName(tournament.name);
      setStartDate(tournament.start_date);
      setEndDate(tournament.end_date);
    } else {
      setName("");
      setStartDate("");
      setEndDate("");
    }
  }, [open, mode, tournament]);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Ange ett namn");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Välj både start- och slutdatum");
      return;
    }
    if (endDate < startDate) {
      toast.error("Slutdatum får inte vara före startdatum");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        start_date: startDate,
        end_date: endDate,
      };
      let data;
      if (mode === "edit" && tournament) {
        const res = await api.put(`/tournaments/${tournament.id}`, payload);
        data = res.data;
        toast.success("Turnering uppdaterad");
      } else {
        const res = await api.post("/tournaments", payload);
        data = res.data;
        toast.success("Turnering skapad");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111] border-[#27272A] text-white"
        data-testid="tournament-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-xl">
            {mode === "edit" ? "Redigera turnering/serie" : "Ny turnering/serie"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Samla matcher under en gemensam serie eller turnering – tidsperioden
            styr vilka matcher som kan ingå.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Namn
            </Label>
            <Input
              data-testid="tournament-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex. Seriespel B-Flickor 2026/2027"
              className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase tracking-widest text-xs">
                Startdatum
              </Label>
              <Input
                data-testid="tournament-start-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-black border-[#3F3F46] text-white h-11 focus-visible:ring-[#34C759] [color-scheme:dark]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase tracking-widest text-xs">
                Slutdatum
              </Label>
              <Input
                data-testid="tournament-end-input"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-black border-[#3F3F46] text-white h-11 focus-visible:ring-[#34C759] [color-scheme:dark]"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="tournament-save-btn"
            onClick={submit}
            disabled={saving || !name.trim() || !startDate || !endDate}
            className="bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
          >
            {mode === "edit" ? "Spara ändringar" : "Skapa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
